import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";

const API = "https://erp-xe9u.onrender.com/api";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const cls = (...c) => c.filter(Boolean).join(" ");

const WO_TYPES = [
  { value: "piecework", label: "Piecework" },
  { value: "hourly", label: "Hourly" },
  { value: "subcontractor", label: "Subcontractor" },
];

const WO_TYPE_LABELS = {
  piecework: "Piecework",
  hourly: "Hourly",
  subcontractor: "Subcontractor",
};

const WO_TYPE_COLORS = {
  piecework: "bg-orange-900/60 text-orange-300",
  hourly: "bg-blue-900/60 text-blue-300",
  subcontractor: "bg-violet-900/60 text-violet-300",
  union_piecework: "bg-orange-900/60 text-orange-300",
  union_hourly: "bg-blue-900/60 text-blue-300",
};

const normalizeType = (t) =>
  t === "union_piecework"
    ? "piecework"
    : t === "union_hourly"
      ? "hourly"
      : t || "piecework";

// ── Helpers ───────────────────────────────────────────────────────────────────
function Spinner({ size = 4 }) {
  return (
    <span
      className={`animate-spin inline-block w-${size} h-${size} border-2 border-white/30 border-t-white rounded-full`}
    />
  );
}

function Badge({ children, color = "gray" }) {
  const map = {
    gray: "bg-gray-700 text-gray-300",
    blue: "bg-blue-900/60 text-blue-300",
    orange: "bg-orange-900/60 text-orange-300",
    purple: "bg-purple-900/60 text-purple-300",
    green: "bg-green-900/60 text-green-300",
    yellow: "bg-yellow-900/60 text-yellow-200",
    violet: "bg-violet-900/60 text-violet-300",
    teal: "bg-teal-900/60 text-teal-200",
  };
  return (
    <span
      className={cls(
        "px-1.5 py-0.5 text-[10px] font-semibold rounded",
        map[color] || map.gray,
      )}
    >
      {children}
    </span>
  );
}

function TypeBadge({ type }) {
  const label = WO_TYPE_LABELS[type] || type || "—";
  const color = WO_TYPE_COLORS[type] || "bg-gray-700 text-gray-300";
  return (
    <span
      className={cls("px-1.5 py-0.5 text-[10px] font-semibold rounded", color)}
    >
      {label}
    </span>
  );
}

// ── Item row ──────────────────────────────────────────────────────────────────
function ItemRow({ item, woId, isSelected, onToggle }) {
  const [expanded, setExpanded] = useState(false);

  const displayQty =
    item.category === "M"
      ? (item.requiredQty ?? item.quantity ?? 0)
      : (item.quantity ?? item.requiredQty ?? 0);

  return (
    <>
      <tr
        className={cls(
          "transition-colors",
          isSelected ? "bg-orange-900/20" : "hover:bg-gray-700/30",
        )}
      >
        {/* Checkbox */}
        <td className="px-2 py-2 text-center w-8">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggle(woId, item._id)}
            className="accent-orange-500 w-3.5 h-3.5 cursor-pointer"
          />
        </td>

        {/* Expand */}
        <td className="px-1 py-2 w-5 text-center">
          <button
            onClick={() => setExpanded((p) => !p)}
            className={cls(
              "text-[10px] transition-transform",
              expanded
                ? "rotate-90 text-orange-400"
                : "text-gray-600 hover:text-gray-400",
            )}
          >
            ▶
          </button>
        </td>

        {/* Source badge */}
        <td className="px-2 py-2">
          {item.source === "change_order" ? (
            <Badge color="purple">CO</Badge>
          ) : (
            <Badge color="blue">BUD</Badge>
          )}
        </td>

        {/* Section */}
        <td className="px-2 py-2">
          {item.sectionNumber ? (
            <span className="text-[9px] font-mono text-indigo-400 bg-indigo-900/40 px-1.5 py-0.5 rounded whitespace-nowrap">
              §{item.sectionNumber}
            </span>
          ) : (
            <span className="text-gray-600">—</span>
          )}
        </td>

        {/* Code */}
        <td className="px-2 py-2 font-mono text-orange-400 text-xs">
          {item.itemCode || "—"}
        </td>

        {/* Description */}
        <td className="px-2 py-2 text-gray-200 text-xs max-w-[220px]">
          <span className="line-clamp-1">
            {item.costCodeDescription || item.itemDescription || "—"}
          </span>
        </td>

        {/* Bid Area */}
        <td className="px-2 py-2 text-gray-400 text-xs whitespace-nowrap">
          {item.bidArea || "—"}
        </td>

        {/* Qty */}
        <td className="px-2 py-2 text-right text-gray-300 font-mono text-xs">
          {displayQty.toLocaleString("en-US", { maximumFractionDigits: 2 })}
        </td>

        {/* UOM */}
        <td className="px-2 py-2 text-center text-gray-400 text-xs">
          {item.uom || "—"}
        </td>

        {/* Rate */}
        <td className="px-2 py-2 text-right text-gray-300 font-mono text-xs">
          ${fmt(item.unitRate ?? item.unitCost ?? 0)}
        </td>

        {/* Amount */}
        <td className="px-2 py-2 text-right font-mono font-semibold text-blue-300 text-xs">
          ${fmt(item.amount || 0)}
        </td>
      </tr>

      {/* Expanded detail */}
      {expanded && (
        <tr className="bg-gray-900/50">
          <td
            colSpan={11}
            className="px-8 py-2 text-[11px] text-gray-400 space-y-0.5"
          >
            <div className="flex flex-wrap gap-x-6 gap-y-0.5">
              <span>
                Mat Code:{" "}
                <span className="text-gray-200 font-mono">
                  {item.matCode || "—"}
                </span>
              </span>
              <span>
                Labor Code:{" "}
                <span className="text-gray-200 font-mono">
                  {item.laborCode || "—"}
                </span>
              </span>
              <span>
                Category:{" "}
                <span className="text-gray-200">{item.category || "—"}</span>
              </span>
              <span>
                Coverage/Unit:{" "}
                <span className="text-gray-200">
                  {item.coveragePerUnit || "—"}
                </span>
              </span>
              {item.source === "change_order" && (
                <span>
                  CO Ref:{" "}
                  <span className="text-purple-300 font-semibold">
                    {item.changeOrderRef || "—"}
                  </span>
                </span>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── WO accordion card ─────────────────────────────────────────────────────────
function WOCard({
  wo,
  allWOs,
  token,
  projectId,
  unionOptions,
  onRename,
  onItemsMoved,
  onDeleted,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [itemsLoaded, setItemsLoaded] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);

  // Rename / edit
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState(wo.title);
  const [editType, setEditType] = useState(normalizeType(wo.type));
  const [editUnion, setEditUnion] = useState(wo.union || "non-union");
  const [renameSaving, setRenameSaving] = useState(false);

  // Selection
  const [selected, setSelected] = useState(new Set());
  const [moving, setMoving] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [secFilter, setSecFilter] = useState("all");
  const [srcFilter, setSrcFilter] = useState("all");
  const [bidFilter, setBidFilter] = useState("all");

  // Delete
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const loadItems = useCallback(async () => {
    setItemsLoading(true);
    try {
      const res = await axios.get(
        `${API}/workorders3/${wo._id}/items?limit=1000`,
        { headers },
      );
      const filtered = (res.data.items || []).filter(
        (i) => i.source === "budget" || i.source === "change_order",
      );
      setItems(filtered);
      setItemsLoaded(true);
    } catch (err) {
      console.error("Failed to load WO items:", err.message);
    } finally {
      setItemsLoading(false);
    }
  }, [wo._id, token]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !itemsLoaded) loadItems();
  };

  // ── Rename/save ────────────────────────────────────────────────────────────
  const handleRename = async () => {
    const val = renameVal.trim();
    if (!val) {
      setRenaming(false);
      return;
    }
    const type = editType;
    const union = type === "subcontractor" ? "non-union" : editUnion;
    setRenameSaving(true);
    try {
      await axios.put(
        `${API}/workorders3/${wo._id}`,
        { title: val, type, union },
        { headers },
      );
      onRename(wo._id, val, type, union);
      setRenaming(false);
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setRenameSaving(false);
    }
  };

  // ── Move ───────────────────────────────────────────────────────────────────
  const handleMove = async (itemIds, targetWoId) => {
    setMoving(true);
    try {
      await Promise.all(
        itemIds.map((itemId) =>
          axios.patch(
            `${API}/budget3/item/${itemId}/relink`,
            { linkedWO3: targetWoId },
            { headers },
          ),
        ),
      );
      await Promise.all(
        [wo._id, targetWoId].map((id) =>
          axios.patch(`${API}/workorders3/${id}/recalculate`, {}, { headers }),
        ),
      );
      setItems((prev) => prev.filter((i) => !itemIds.includes(i._id)));
      // Reset all filters after move
      setSearch("");
      setSecFilter("all");
      setSrcFilter("all");
      setBidFilter("all");
      setSelected(new Set());
      onItemsMoved(wo._id, targetWoId, itemIds);
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setMoving(false);
    }
  };

  const handleMoveSelected = (targetWoId) => {
    if (!selected.size) return;
    handleMove([...selected], targetWoId);
  };

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/workorders3/${wo._id}`, { headers });
      onDeleted(wo._id);
    } catch (err) {
      alert(err.response?.data?.message || err.message);
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  // ── Selection ──────────────────────────────────────────────────────────────
  const toggleItem = (_, itemId) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(itemId) ? next.delete(itemId) : next.add(itemId);
      return next;
    });

  const toggleAll = () => {
    if (selected.size === filteredItems.length) setSelected(new Set());
    else setSelected(new Set(filteredItems.map((i) => i._id)));
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const uniqueSections = useMemo(
    () =>
      [
        "all",
        ...new Set(items.map((i) => i.sectionNumber).filter(Boolean)),
      ].sort(),
    [items],
  );

  const uniqueBidAreas = useMemo(
    () =>
      ["all", ...new Set(items.map((i) => i.bidArea).filter(Boolean))].sort(),
    [items],
  );

  const hasCO = items.some((i) => i.source === "change_order");

  const filteredItems = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter((item) => {
      const matchSec = secFilter === "all" || item.sectionNumber === secFilter;
      const matchBid = bidFilter === "all" || item.bidArea === bidFilter;
      const matchSrc = srcFilter === "all" || item.source === srcFilter;
      const matchQ =
        !q ||
        (item.costCodeDescription || item.itemDescription || "")
          .toLowerCase()
          .includes(q) ||
        (item.itemCode || "").toLowerCase().includes(q) ||
        (item.bidArea || "").toLowerCase().includes(q);
      return matchSec && matchBid && matchSrc && matchQ;
    });
  }, [items, search, secFilter, bidFilter, srcFilter]);

  const totals = useMemo(
    () => filteredItems.reduce((a, i) => a + (i.amount || 0), 0),
    [filteredItems],
  );

  const hasActiveFilter =
    search || secFilter !== "all" || srcFilter !== "all" || bidFilter !== "all";

  const clearFilters = () => {
    setSearch("");
    setSecFilter("all");
    setSrcFilter("all");
    setBidFilter("all");
  };

  const targetWOs = allWOs.filter((w) => w._id !== wo._id);
  const canDelete =
    itemsLoaded &&
    items.length === 0 &&
    (wo.originalEstimatedAmount || 0) === 0;

  const typeColor =
    {
      piecework: "bg-orange-900/50 text-orange-300",
      hourly: "bg-blue-900/50 text-blue-300",
      subcontractor: "bg-violet-900/50 text-violet-300",
      union_piecework: "bg-orange-900/50 text-orange-300",
      union_hourly: "bg-blue-900/50 text-blue-300",
    }[wo.type] || "bg-gray-700 text-gray-400";

  return (
    <div
      className={cls(
        "rounded-xl border overflow-hidden transition-all",
        open
          ? "border-orange-600/50 bg-gray-800"
          : "border-gray-700 bg-gray-800",
      )}
    >
      {/* ── Header ── */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Collapse toggle */}
        <button
          onClick={handleToggle}
          className={cls(
            "text-[11px] transition-transform shrink-0",
            open
              ? "rotate-90 text-orange-400"
              : "text-gray-500 hover:text-gray-300",
          )}
        >
          ▶
        </button>

        {/* WO number */}
        <span className="text-[10px] font-mono text-gray-500 shrink-0">
          #{wo.woNumber}
        </span>

        {/* Title / rename */}
        {renaming ? (
          <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
            <input
              autoFocus
              value={renameVal}
              onChange={(e) => setRenameVal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRename();
                if (e.key === "Escape") {
                  setRenaming(false);
                  setRenameVal(wo.title);
                }
              }}
              className="flex-1 min-w-[140px] px-2 py-1 text-sm bg-gray-700 border border-orange-500 rounded text-gray-100 focus:outline-none"
            />
            <select
              value={editType}
              onChange={(e) => {
                setEditType(e.target.value);
                if (e.target.value === "subcontractor")
                  setEditUnion("non-union");
              }}
              className="px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none"
            >
              {WO_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <select
              value={editUnion}
              disabled={editType === "subcontractor"}
              onChange={(e) => setEditUnion(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 focus:border-orange-500 focus:outline-none disabled:opacity-40"
            >
              {unionOptions.map((u) => (
                <option key={u.value} value={u.value}>
                  {u.label}
                </option>
              ))}
            </select>
            <button
              onClick={handleRename}
              disabled={renameSaving}
              className="px-2.5 py-1 text-xs bg-orange-500 hover:bg-orange-600 text-white rounded font-semibold disabled:opacity-50 flex items-center gap-1"
            >
              {renameSaving ? <Spinner size={3} /> : "Save"}
            </button>
            <button
              onClick={() => {
                setRenaming(false);
                setRenameVal(wo.title);
              }}
              className="px-2 py-1 text-xs text-gray-400 hover:text-white border border-gray-600 rounded"
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-sm font-semibold text-white truncate">
              {wo.title}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setEditType(normalizeType(wo.type));
                setEditUnion(wo.union || "non-union");
                setRenameVal(wo.title);
                setRenaming(true);
              }}
              className="shrink-0 text-[10px] text-gray-500 hover:text-orange-400 transition-colors"
              title="Edit WO"
            >
              ✏
            </button>
          </div>
        )}

        {/* Meta */}
        <div className="flex items-center gap-2 shrink-0 ml-auto">
          <span
            className={cls(
              "text-[10px] px-1.5 py-0.5 rounded font-semibold",
              typeColor,
            )}
          >
            {WO_TYPE_LABELS[wo.type] || wo.type || "—"}
          </span>
          {wo.union &&
            wo.union !== "non-union" &&
            wo.type !== "subcontractor" && (
              <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-teal-900/60 text-teal-200">
                {wo.union}
              </span>
            )}
          <span
            className={cls(
              "text-[10px] px-1.5 py-0.5 rounded font-semibold",
              wo.status === "active"
                ? "bg-green-900/50 text-green-300"
                : "bg-gray-700 text-gray-400",
            )}
          >
            {wo.status || "active"}
          </span>
          <span className="text-sm font-bold text-green-300 font-mono">
            ${fmt(wo.originalEstimatedAmount || 0)}
          </span>

          {/* Delete */}
          {itemsLoaded &&
            (!confirmDelete ? (
              <button
                onClick={() => {
                  if (canDelete) setConfirmDelete(true);
                  else
                    alert(`"${wo.title}" has items. Move all items out first.`);
                }}
                className={cls(
                  "text-[10px] transition-colors ml-1",
                  canDelete
                    ? "text-gray-600 hover:text-red-400"
                    : "text-gray-800 cursor-not-allowed",
                )}
                title={
                  canDelete ? "Delete WO" : "Move all items out before deleting"
                }
              >
                🗑
              </button>
            ) : (
              <div className="flex items-center gap-1 bg-red-950 border border-red-700 rounded px-2 py-0.5">
                <span className="text-[10px] text-red-300">Delete?</span>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="text-[10px] text-red-300 hover:text-red-100 font-semibold disabled:opacity-50"
                >
                  {deleting ? <Spinner size={3} /> : "Yes"}
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="text-[10px] text-gray-400 hover:text-white"
                >
                  No
                </button>
              </div>
            ))}
        </div>
      </div>

      {/* ── Expanded body ── */}
      {open && (
        <div className="border-t border-gray-700">
          {itemsLoading ? (
            <div className="flex items-center justify-center py-8 gap-2">
              <Spinner />
              <span className="text-xs text-gray-400">Loading items…</span>
            </div>
          ) : (
            <>
              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-gray-700/60 bg-gray-900/30">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search items…"
                  className="px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none w-40"
                />

                {/* Section filter */}
                {uniqueSections.length > 2 && (
                  <select
                    value={secFilter}
                    onChange={(e) => setSecFilter(e.target.value)}
                    className="px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-300 focus:border-orange-500 focus:outline-none"
                  >
                    {uniqueSections.map((s) => (
                      <option key={s} value={s}>
                        {s === "all" ? "All Sections" : `§${s}`}
                      </option>
                    ))}
                  </select>
                )}

                {/* Bid area filter */}
                {uniqueBidAreas.length > 2 && (
                  <select
                    value={bidFilter}
                    onChange={(e) => setBidFilter(e.target.value)}
                    className="px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-300 focus:border-orange-500 focus:outline-none"
                  >
                    {uniqueBidAreas.map((b) => (
                      <option key={b} value={b}>
                        {b === "all" ? "All Bid Areas" : b}
                      </option>
                    ))}
                  </select>
                )}

                {/* Source filter */}
                <div className="flex rounded overflow-hidden border border-gray-600">
                  {[
                    { v: "all", l: "All" },
                    { v: "budget", l: "Bud" },
                    ...(hasCO ? [{ v: "change_order", l: "CO" }] : []),
                  ].map(({ v, l }) => (
                    <button
                      key={v}
                      onClick={() => setSrcFilter(v)}
                      className={cls(
                        "px-2 py-1 text-[10px] font-medium transition-colors",
                        srcFilter === v
                          ? "bg-orange-500 text-white"
                          : "bg-gray-700 text-gray-400 hover:bg-gray-600",
                      )}
                    >
                      {l}
                    </button>
                  ))}
                </div>

                <span className="text-xs text-gray-500">
                  {filteredItems.length} / {items.length} items
                </span>

                {hasActiveFilter && (
                  <button
                    onClick={clearFilters}
                    className="text-[10px] text-orange-400 hover:text-orange-300 underline"
                  >
                    Clear filters
                  </button>
                )}

                <span className="text-xs font-mono font-bold text-blue-300 ml-auto">
                  ${fmt(totals)}
                </span>
              </div>

              {/* Bulk action bar */}
              {selected.size > 0 && (
                <div className="flex items-center gap-3 px-4 py-2 bg-orange-950/40 border-b border-orange-700/40">
                  <span className="text-xs text-orange-300 font-semibold">
                    {selected.size} item{selected.size !== 1 ? "s" : ""}{" "}
                    selected
                  </span>
                  {targetWOs.length > 0 && (
                    <>
                      <span className="text-xs text-gray-400">Move to:</span>
                      {targetWOs.map((two) => (
                        <button
                          key={two._id}
                          onClick={() => handleMoveSelected(two._id)}
                          disabled={moving}
                          className="px-2.5 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-gray-200 rounded border border-gray-600 disabled:opacity-50 flex items-center gap-1"
                        >
                          {moving && <Spinner size={3} />}
                          {two.title}
                        </button>
                      ))}
                    </>
                  )}
                  <button
                    onClick={() => setSelected(new Set())}
                    className="ml-auto text-[10px] text-gray-500 hover:text-white"
                  >
                    ✕ Clear
                  </button>
                </div>
              )}

              {/* Items table */}
              <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-900/80 sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-2 w-8">
                        <input
                          type="checkbox"
                          checked={
                            filteredItems.length > 0 &&
                            selected.size === filteredItems.length
                          }
                          onChange={toggleAll}
                          className="accent-orange-500 w-3.5 h-3.5 cursor-pointer"
                        />
                      </th>
                      <th className="w-5" />
                      <th className="px-2 py-2 text-left text-gray-500 font-medium">
                        Type
                      </th>
                      <th className="px-2 py-2 text-left text-gray-500 font-medium">
                        Sec
                      </th>
                      <th className="px-2 py-2 text-left text-gray-500 font-medium">
                        Code
                      </th>
                      <th className="px-2 py-2 text-left text-gray-500 font-medium min-w-[180px]">
                        Description
                      </th>
                      <th className="px-2 py-2 text-left text-gray-500 font-medium">
                        Bid Area
                      </th>
                      <th className="px-2 py-2 text-right text-gray-500 font-medium">
                        Qty
                      </th>
                      <th className="px-2 py-2 text-center text-gray-500 font-medium">
                        UOM
                      </th>
                      <th className="px-2 py-2 text-right text-gray-500 font-medium">
                        Rate
                      </th>
                      <th className="px-2 py-2 text-right text-gray-500 font-medium">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700/40">
                    {filteredItems.length === 0 ? (
                      <tr>
                        <td
                          colSpan={11}
                          className="px-4 py-8 text-center text-gray-500 italic"
                        >
                          {items.length === 0
                            ? "No budget items in this work order."
                            : "No items match your filters."}
                        </td>
                      </tr>
                    ) : (
                      filteredItems.map((item) => (
                        <ItemRow
                          key={item._id}
                          item={item}
                          woId={wo._id}
                          isSelected={selected.has(item._id)}
                          onToggle={toggleItem}
                        />
                      ))
                    )}
                  </tbody>
                  {filteredItems.length > 0 && (
                    <tfoot className="sticky bottom-0 bg-gray-900/90 border-t border-gray-700">
                      <tr>
                        <td
                          colSpan={10}
                          className="px-2 py-2 text-xs text-gray-500"
                        >
                          {filteredItems.length} items shown
                        </td>
                        <td className="px-2 py-2 text-right font-mono font-bold text-blue-300">
                          ${fmt(totals)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Main panel
// ═════════════════════════════════════════════════════════════════════════════
export default function EditWOsPanel({
  projectId,
  token,
  unions,
  onClose,
  onSaved,
}) {
  const [wos, setWOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  // New WO
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newType, setNewType] = useState("piecework");
  const [newUnion, setNewUnion] = useState("non-union");
  const [creating, setCreating] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const unionOptions = useMemo(() => {
    const company = (unions || []).map((u) => u.title).filter(Boolean);
    const fromWOs = wos
      .map((w) => w.union)
      .filter((u) => u && u !== "non-union");
    const all = [...new Set([...company, ...fromWOs])];
    return [
      ...all.map((title) => ({ value: title, label: title })),
      { value: "non-union", label: "Non-Union" },
    ];
  }, [unions, wos]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/workorders3/project/${projectId}`, {
        headers,
      });
      setWOs(res.data.workOrders || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRename = (woId, title, type, union) => {
    setWOs((prev) =>
      prev.map((w) => (w._id === woId ? { ...w, title, type, union } : w)),
    );
    onSaved?.();
  };

  const handleItemsMoved = (fromId, toId) => {
    // Reload WOs to get updated amounts
    load();
    onSaved?.();
  };

  const handleDeleted = (woId) => {
    setWOs((prev) => prev.filter((w) => w._id !== woId));
    onSaved?.();
  };

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const union = newType === "subcontractor" ? "non-union" : newUnion;
      const res = await axios.post(
        `${API}/workorders3/project/${projectId}`,
        { title: newTitle.trim(), type: newType, union },
        { headers },
      );
      setWOs((prev) => [...prev, res.data.workOrder]);
      setNewTitle("");
      setNewType("piecework");
      setNewUnion("non-union");
      setShowCreate(false);
      onSaved?.();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setCreating(false);
    }
  };

  const filteredWOs = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return wos;
    return wos.filter(
      (w) =>
        w.title.toLowerCase().includes(q) ||
        (w.woNumber || "").toString().includes(q) ||
        (w.type || "").toLowerCase().includes(q) ||
        (w.union || "").toLowerCase().includes(q),
    );
  }, [wos, search]);

  const grandTotal = useMemo(
    () => wos.reduce((s, w) => s + (w.originalEstimatedAmount || 0), 0),
    [wos],
  );

  return (
    <div className="fixed inset-0 z-[99998] flex flex-col bg-gray-950/95 backdrop-blur-sm">
      {/* Header */}
      <div className="flex items-center gap-4 px-6 py-4 border-b border-gray-700 bg-gray-900 shrink-0">
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-bold text-white">Edit Work Orders</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {wos.length} WO{wos.length !== 1 ? "s" : ""} · Grand total:{" "}
            <span className="text-green-300 font-semibold font-mono">
              ${fmt(grandTotal)}
            </span>
          </p>
        </div>

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter WOs…"
          className="px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none w-48"
        />

        <button
          onClick={() => setShowCreate((p) => !p)}
          className="px-3 py-1.5 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
        >
          + New WO
        </button>

        <button
          onClick={onClose}
          className="px-3 py-1.5 text-sm text-gray-400 hover:text-white bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
        >
          ✕ Close
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="shrink-0 px-6 py-4 border-b border-gray-700 bg-gray-800/60">
          <div className="flex flex-wrap items-end gap-3 max-w-3xl">
            <div className="flex-1 min-w-[180px]">
              <label className="block text-[10px] text-gray-400 mb-1">
                Title *
              </label>
              <input
                autoFocus
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                placeholder="e.g. Level 3 — Drywall"
                className="w-full px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
              />
            </div>
            <div className="w-36">
              <label className="block text-[10px] text-gray-400 mb-1">
                Type
              </label>
              <select
                value={newType}
                onChange={(e) => {
                  setNewType(e.target.value);
                  if (e.target.value === "subcontractor")
                    setNewUnion("non-union");
                }}
                className="w-full px-2 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
              >
                {WO_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-36">
              <label className="block text-[10px] text-gray-400 mb-1">
                Union
              </label>
              <select
                value={newUnion}
                disabled={newType === "subcontractor"}
                onChange={(e) => setNewUnion(e.target.value)}
                className="w-full px-2 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none disabled:opacity-40"
              >
                {unionOptions.map((u) => (
                  <option key={u.value} value={u.value}>
                    {u.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={handleCreate}
              disabled={!newTitle.trim() || creating}
              className="px-4 py-1.5 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {creating ? <Spinner size={3} /> : null}
              Create
            </button>
            <button
              onClick={() => {
                setShowCreate(false);
                setNewTitle("");
              }}
              className="px-3 py-1.5 text-sm text-gray-400 hover:text-white border border-gray-600 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="mx-6 mt-3 flex items-center justify-between px-4 py-2 bg-red-900/40 border border-red-700/60 rounded-lg text-xs text-red-300 shrink-0">
          <span>{error}</span>
          <button
            onClick={() => setError("")}
            className="ml-3 text-red-500 hover:text-red-300"
          >
            ✕
          </button>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-3">
            <Spinner />
            <span className="text-gray-400">Loading work orders…</span>
          </div>
        ) : filteredWOs.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            {wos.length === 0
              ? "No work orders found."
              : "No WOs match your filter."}
          </div>
        ) : (
          filteredWOs.map((wo) => (
            <WOCard
              key={wo._id}
              wo={wo}
              allWOs={wos}
              token={token}
              projectId={projectId}
              unionOptions={unionOptions}
              onRename={handleRename}
              onItemsMoved={handleItemsMoved}
              onDeleted={handleDeleted}
            />
          ))
        )}
      </div>

      {/* Footer */}
      <div className="shrink-0 px-6 py-3 border-t border-gray-700 bg-gray-900 flex items-center justify-between">
        <p className="text-xs text-gray-500">
          💡 Click ▶ on any WO to expand its items. Use checkboxes to bulk-move
          items between WOs.
        </p>
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
        >
          Done
        </button>
      </div>
    </div>
  );
}
